import {
    Component,
    computed,
    ElementRef,
    EventEmitter,
    Output,
    Signal,
    ViewChild,
} from '@angular/core'
import {
    SESSION_ACTIVITY_TYPE_ANYTHING,
    SESSION_ACTIVITY_TYPE_DESK,
    SESSION_ACTIVITY_TYPE_MOVING,
    SESSION_DURATION_25_MINS,
    SESSION_DURATION_50_MINS,
    SESSION_DURATION_75_MINS,
    SessionActivityType,
} from '@app/core/_models'
import { SelectButtonChangeEvent, SelectButtonModule } from 'primeng/selectbutton'
import {
    faLampDesk,
    faPersonWalking,
    faShuffle,
    faMicrophoneSlash,
    faStar,
} from '@fortawesome/pro-solid-svg-icons'
import { FormsModule } from '@angular/forms'
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome'
import { SessionSettingsService } from '@app/core/_services/session-settings.service'
import { ToggleSwitchModule } from 'primeng/toggleswitch'
import { CommonModule } from '@angular/common'
import { ButtonModule } from 'primeng/button'
import { LocalStorageAccessService } from '@app/core/_services/local-storage-access.service'
import { ViewportService } from '@app/core/_services/viewport.service'
import { UtilsService } from '@app/core/_services'
import { DialogService } from '@app/core/_services/dialog.service'
import { TooltipModule } from 'primeng/tooltip'
import { BookingHubQuietModeMatchHintComponent } from './booking-hub-quiet-mode-match-hint.component'

interface ActivityOption {
    icon: any
    label: string
    value: SessionActivityType
    tooltip: string
    next: number
}

const ACTIVITY_OPTIONS: ActivityOption[] = [
    {
        icon: faLampDesk,
        tooltip: 'My Task: Desk',
        label: 'Desk',
        value: SESSION_ACTIVITY_TYPE_DESK,
        next: 1,
    },
    {
        icon: faPersonWalking,
        tooltip: 'My Task: Moving',
        label: 'Moving',
        value: SESSION_ACTIVITY_TYPE_MOVING,
        next: 2,
    },
    {
        icon: faShuffle,
        tooltip: 'My Task: Anything',
        label: 'Anything',
        value: SESSION_ACTIVITY_TYPE_ANYTHING,
        next: 0,
    },
]

interface DurationOption {
    label: string
    value: number
    tooltip: string
    next: number
}

const DURATION_OPTIONS: DurationOption[] = [
    { label: '25', tooltip: 'Duration: 25 mins', value: SESSION_DURATION_25_MINS, next: 1 },
    { label: '50', tooltip: 'Duration: 50 mins', value: SESSION_DURATION_50_MINS, next: 2 },
    { label: '75', tooltip: 'Duration: 75 mins', value: SESSION_DURATION_75_MINS, next: 0 },
]

@Component({
    selector: 'fm-booking-hub-session-settings',
    imports: [
        CommonModule,
        ButtonModule,
        SelectButtonModule,
        ToggleSwitchModule,
        FormsModule,
        FontAwesomeModule,
        TooltipModule,
        BookingHubQuietModeMatchHintComponent,
    ],
    templateUrl: './booking-hub-session-settings.component.html',
    styleUrl: './booking-hub-session-settings.component.scss',
})
export class BookingHubSessionSettingsComponent {
    @ViewChild('container') container: ElementRef
    @Output() settingsExpanded: EventEmitter<boolean> = new EventEmitter<boolean>()

    public expandSettingsPanel = false

    // Activity
    public activityOptions = ACTIVITY_OPTIONS
    public currentActivity: ActivityOption
    public nextActivity: ActivityOption // only used for animation
    public animatingActivityLabel = false

    // Duration
    public durationOptions = DURATION_OPTIONS
    public currentDuration: DurationOption
    public nextDuration: DurationOption // only used for animation
    public animatingDurationLabel = false

    // Quiet Mode
    public quietMode: boolean
    public readonly preferFavorites = computed(
        () => this.sessionSettingsService.preferFavoritesSetting() === 'preferred',
    )
    public faMicrophoneSlash = faMicrophoneSlash
    public faStar = faStar

    constructor(
        public viewportService: ViewportService,
        private localStorageAccessService: LocalStorageAccessService,
        public sessionSettingsService: SessionSettingsService,
        private utilsService: UtilsService,
        private dialogService: DialogService,
    ) {
        this.quietMode = this.sessionSettingsService.quietModeEnabled()
    }

    ngOnInit() {
        // get the initial duration
        const duration = this.sessionSettingsService.sessionDurationSetting()
        this.currentDuration = this.durationOptions.find((item) => item.value === duration)
        this.nextDuration = this.durationOptions[this.currentDuration.next]

        // get the initial activity
        const activity = this.sessionSettingsService.getSessionSettingActivityType()
        this.currentActivity = this.activityOptions.find((item) => item.value === activity)
        this.nextActivity = this.activityOptions[this.currentActivity.next]

        const panelExpandedStoredValue = this.localStorageAccessService.getItem(
            'sessionSettingsPanel:expanded',
        )
        this.expandSettingsPanel =
            panelExpandedStoredValue === 'true' || panelExpandedStoredValue == null
        this.settingsExpanded.emit(this.expandSettingsPanel)
    }

    onClickToggleSessionSettingsPanel(): void {
        this.utilsService.animateHeightChange(
            this.container.nativeElement,
            () => (this.expandSettingsPanel = !this.expandSettingsPanel),
        )

        this.localStorageAccessService.setItem(
            'sessionSettingsPanel:expanded',
            this.expandSettingsPanel.toString(),
        )
        this.settingsExpanded.emit(this.expandSettingsPanel)
    }

    toggleDuration() {
        if (this.animatingDurationLabel) {
            return
        }
        this.animatingDurationLabel = true

        setTimeout(() => {
            this.currentDuration = this.nextDuration
            this.nextDuration = this.durationOptions[this.nextDuration.next]
            this.animatingDurationLabel = false
            this.sessionSettingsService.setSessionDurationSetting(this.currentDuration.value)
        }, 200)
    }

    onSelectedDuration(event: SelectButtonChangeEvent): void {
        this.currentDuration = this.durationOptions.find((element) => element.value == event.value)
        this.sessionSettingsService.setSessionDurationSetting(this.currentDuration.value)
    }

    toggleActivity() {
        if (this.animatingActivityLabel) {
            return
        }
        this.animatingActivityLabel = true

        setTimeout(() => {
            this.currentActivity = this.nextActivity
            this.nextActivity = this.activityOptions[this.nextActivity.next]
            this.animatingActivityLabel = false
            this.sessionSettingsService.setSessionSettingActivityType(this.currentActivity.value)
        }, 200)
    }

    onSelectedActivity(event: SelectButtonChangeEvent): void {
        const selectedActivity = this.activityOptions.find(
            (element) => element.value == event.value,
        )
        if (!selectedActivity) {
            return
        }

        this.currentActivity = selectedActivity
        this.sessionSettingsService.setSessionSettingActivityType(this.currentActivity.value)
    }

    disableQuietMode(): void {
        this.sessionSettingsService.setSessionSettingQuietMode(false)
        this.quietMode = false
    }

    disableFavoritesPreference(): void {
        this.sessionSettingsService.setSessionSettingFavoritesPreference('noPreference')
    }

    onToggleQuietMode(event: any) {
        this.sessionSettingsService.setSessionSettingQuietMode(event.checked)
    }

    onTogglePreferFavorites(event: any) {
        if (event.checked) {
            this.sessionSettingsService.setSessionSettingFavoritesPreference('preferred')
        } else {
            this.sessionSettingsService.setSessionSettingFavoritesPreference('noPreference')
        }
    }

    onClickInfoCircle(): void {
        this.dialogService.openSessionPreferencesInfoModal()
    }
}
